/**
 * A IMAGEM SAI DO DOCUMENTO DA PÁGINA E VIRA DOCUMENTO IRMÃO.
 *
 * ── O PONTO, E ELE É O ÚNICO BLOQUEIO DA RODADA (`T6.6`) ─────────────────────────────────────
 *
 * Ele, na letra: *"A página 1 ficou grande demais. Olha só, tem limite de tamanho de página. Eu
 * não consigo publicar. Isso aqui é absurdo. Ficou 1.20 MB. Já não pode publicar porque tem 1.20
 * MB. (…) isso tem que ser aumentado urgentemente. No mínimo 5 MB. E nas páginas que tiverem 5 ou
 * 6 imagens acabou, nunca vai postar."*
 *
 * A frase que ele leu na tela — *"A página 1 ficou grande demais para publicar (1.20 MB)"* — é a
 * nossa, de `motivoDeRecusaPorTamanho`. O teto era `TETO_POR_PAGINA = 700 KiB`, escolha nossa.
 *
 * ── POR QUE SUBIR O TETO PARA 5 MB NÃO RESOLVE, E ISSO FOI MEDIDO ────────────────────────────
 *
 * O número dele é o número certo para o **objetivo** e o número errado para o **mecanismo**:
 * mudar `TETO_POR_PAGINA` não move o limite, só muda quem recusa. Medido em 17/09 contra o
 * emulador do próprio projeto, gravando um documento com o campo crescendo:
 *
 * ```
 *    700 KiB  ->  HTTP 403   barrado pela REGRA (a coleção de teste não tem rule) — o tamanho passou
 *   1000 KiB  ->  HTTP 403   idem
 *   1229 KiB  ->  HTTP 400   "The value of property "imagem" is longer than 1048487 bytes."
 *   5120 KiB  ->  HTTP 400   o mesmo
 * ```
 *
 * **1.048.487 bytes**, e o limite é de quem guarda. A página dele, de 1,20 MB, já está acima —
 * então com o teto em 5 MB o publicar passaria pela nossa checagem e morreria no meio da
 * operação, com a ficha já escrita. É exatamente o defeito do `T4.13`, que `plano-de-publicacao.ts`
 * existe para impedir.
 *
 * ── O CAMINHO QUE EU NÃO PUDE TOMAR, E POR QUE ───────────────────────────────────────────────
 *
 * O cabeçalho de `publicacao.ts` já dizia, dois pedidos atrás, qual era o conserto de verdade:
 * *"guardar imagem fora do Firestore — Storage, com URL pública"*. Medido em 17/09:
 *
 * ```
 * GET firebasestorage.googleapis.com/v0/b/hq-lab-afd38.appspot.com/o/characters%2Fchar1.png
 *   ->  HTTP 402  "Cloud Storage for Firebase no longer supports Firebase projects that are on
 *                  the no-cost Spark pricing plan. Please upgrade to the pay-as-you-go Blaze..."
 * ```
 *
 * O Storage está **indisponível no plano de cobrança do projeto**. Isso é dinheiro, e é decisão do
 * operador — não minha. Fica dito no `PL-T6.1`.
 *
 * ── O QUE DÁ PARA FAZER HOJE, SEM DINHEIRO E SEM REGRA NOVA ──────────────────────────────────
 *
 * O limite é **por documento**, e este produto já guarda **uma página por documento**
 * (`biblioteca/{id}/paginas/{n}`). Então a saída é quebrar mais fundo: **cada imagem vira um
 * documento irmão da página.**
 *
 * Três coisas fazem isso funcionar sem tocar em nada que exija deploy:
 *
 *   1. **A regra já autoriza.** `match /paginas/{pagina}` é curinga de id — ela exige conta,
 *      autoria e não-finalizado, e **nada** sobre o id ou a forma do documento. Documento irmão
 *      passa pela regra que já está em produção.
 *   2. **A indireção já existe e ninguém a usou.** O elemento de imagem carrega `refImagem:
 *      string`, um campo de REFERÊNCIA — e o que foi posto lá até hoje foi o data URI inteiro.
 *      É o mesmo padrão que este épico já achou nos botões de camada, nas fontes e na semente:
 *      capacidade completa de ponta a ponta, com as duas pontas sem ligar.
 *   3. **A leitura já traz a coleção inteira.** `ler-historia.tsx` faz
 *      `getCollection("biblioteca/{id}/paginas")`, então os documentos de imagem chegam no mesmo
 *      pacote — sem uma ida a mais na rede por imagem.
 *
 * Cada imagem já cabe com folga num documento, porque o `encolherParaCaber` do `T5.6` a deixa em
 * no máximo 900 KiB antes de ela chegar aqui. Uma página com 5 ou 6 imagens vira 6 ou 7
 * documentos legais — que é o que ele pediu, na frase em que disse o motivo do número.
 */
import { Pagina } from "../api/documento.types";

/**
 * O teto REAL de um documento, medido — não o documentado.
 *
 * `1048487` é o número que o Firestore devolveu na mensagem de recusa. Não é `1024*1024`
 * (1.048.576): há um desconto de 89 bytes para o nome do documento e os metadados, e escrever
 * `1024*1024` aqui seria errar por 89 bytes na única linha em que 89 bytes decidem.
 */
export const TETO_DE_DOCUMENTO = 1_048_487;

/**
 * A folga, e ela não é medo: é o resto do documento.
 *
 * O teto acima é do documento INTEIRO, não do campo. Um documento de imagem carrega, além do data
 * URI, o id do elemento e o número da página. 8 KiB cobrem isso com sobra.
 */
export const FOLGA_DO_DOCUMENTO = 8 * 1024;

/** O que um `refImagem` passa a carregar quando a imagem saiu para outro documento. */
export const MARCA = "doc-imagem:";

/** Um documento de imagem, irmão da página. */
export interface DocumentoDeImagem {
  /** O id do documento dentro de `biblioteca/{id}/paginas`. */
  id: string;
  /** Sempre `"imagem"`, e é por este campo que a leitura separa imagem de página. */
  tipo: "imagem";
  /** A página a que ela pertence, para quem for depurar pelo console do Firebase. */
  pagina: number;
  /** O data URI. */
  dado: string;
}

/** É um documento de imagem, e não uma página? */
export function ehDocumentoDeImagem(d: unknown): d is DocumentoDeImagem {
  const x = d as DocumentoDeImagem | null;
  return !!x && x.tipo === "imagem" && typeof x.dado === "string" && typeof x.id === "string";
}

/** Quanto pesa um documento serializado. Mesma conta de `pesoDaPagina`, e pelo mesmo motivo. */
export function pesoDoDocumento(d: unknown): number {
  return JSON.stringify(d).length;
}

/**
 * O id do documento de uma imagem.
 *
 * Prefixo `p{n}-img-` e não só um número: o id tem que ser legível por quem abrir o console do
 * Firebase para entender por que uma página tem sete documentos. E não pode colidir com o id de
 * página, que é `String(numero)` — daí o `p`.
 */
export function idDaImagem(pagina: number, ordem: number): string {
  return `p${pagina}-img-${ordem}`;
}

export interface PaginaSeparada {
  /** A página sem os data URIs — cada `refImagem` virou uma marca. */
  pagina: Pagina;
  /** Os documentos irmãos a gravar. Vazio quando a página não tinha imagem embutida. */
  imagens: DocumentoDeImagem[];
}

/**
 * Separa os data URIs de uma página em documentos irmãos.
 *
 * **Só toca em `refImagem` que comece com `data:`.** Uma referência que já é `http` (ou já é
 * marca) fica como está: ela não pesa, e reescrevê-la seria mexer no que não é o problema.
 *
 * Não muda a página recebida — devolve outra. Publicar lê o documento do editor, e mutá-lo faria
 * a tela perder as imagens enquanto a pessoa ainda está olhando para ela.
 */
export function separarImagens(pagina: Pagina, numero: number): PaginaSeparada {
  const imagens: DocumentoDeImagem[] = [];
  let ordem = 0;

  const copia: Pagina = {
    ...pagina,
    camadas: (pagina.camadas ?? []).map((camada) => ({
      ...camada,
      elementos: (camada.elementos ?? []).map((el) => {
        const e = el as unknown as { tipo?: string; refImagem?: string };
        if (e.tipo !== "imagem" || typeof e.refImagem !== "string") return el;
        if (!e.refImagem.startsWith("data:")) return el;

        const id = idDaImagem(numero, ordem++);
        imagens.push({ id, tipo: "imagem", pagina: numero, dado: e.refImagem });
        return { ...el, refImagem: `${MARCA}${id}` };
      }),
    })),
  };

  return { pagina: copia, imagens };
}

/**
 * O contrário: devolve os data URIs para dentro da página, na leitura.
 *
 * Uma marca sem documento correspondente fica como está, em vez de virar string vazia. O motivo é
 * de tela: `enderecoDaImagem` já sabe não desenhar uma referência que não reconhece, e uma imagem
 * que falta aparece como quadro vazio. Uma string vazia apareceria como imagem quebrada — pior,
 * porque parece defeito de rede e convida a recarregar.
 */
export function juntarImagens(pagina: Pagina, imagens: DocumentoDeImagem[]): Pagina {
  if (!imagens.length) return pagina;
  const porId = new Map(imagens.map((i) => [i.id, i.dado]));

  return {
    ...pagina,
    camadas: (pagina.camadas ?? []).map((camada) => ({
      ...camada,
      elementos: (camada.elementos ?? []).map((el) => {
        const e = el as unknown as { tipo?: string; refImagem?: string };
        if (e.tipo !== "imagem" || typeof e.refImagem !== "string") return el;
        if (!e.refImagem.startsWith(MARCA)) return el;

        const dado = porId.get(e.refImagem.slice(MARCA.length));
        return dado ? { ...el, refImagem: dado } : el;
      }),
    })),
  };
}

/**
 * Separa a coleção lida em páginas e imagens, e remonta.
 *
 * A leitura de `biblioteca/{id}/paginas` traz tudo junto — é por isso que quebrar em documentos
 * irmãos não custa uma ida a mais na rede. Esta função é o que a tela chama no lugar de confiar
 * que tudo o que veio é página.
 */
export function paginasLidas(documentos: unknown[]): Pagina[] {
  const imagens = documentos.filter(ehDocumentoDeImagem);
  const paginas = documentos.filter((d) => !ehDocumentoDeImagem(d)) as Pagina[];
  return paginas.map((p) => juntarImagens(p, imagens));
}

/**
 * O que ainda não cabe depois de separar as imagens — e agora é outra coisa.
 *
 * Com as imagens fora, uma página que ainda passa de 1 MiB é uma página de TEXTO com mais de um
 * megabyte, o que nenhuma criança escreve à mão. Se acontecer, é dado corrompido ou colagem de
 * máquina, e a mensagem não deve mandar "publique com menos desenhos" — que era o conselho certo
 * para o defeito antigo e virou conselho errado para este.
 *
 * E o mesmo vale para uma IMAGEM que não cabe sozinha: o `encolherParaCaber` do `T5.6` a deixa em
 * 900 KiB, então uma imagem acima do teto significa que ela entrou sem passar por lá.
 */
export function motivoDeRecusaDepoisDeSeparar(separadas: PaginaSeparada[]): string | null {
  const teto = TETO_DE_DOCUMENTO - FOLGA_DO_DOCUMENTO;

  for (let i = 0; i < separadas.length; i++) {
    const { pagina, imagens } = separadas[i];

    if (pesoDoDocumento(pagina) > teto) {
      const mb = (pesoDoDocumento(pagina) / 1024 / 1024).toFixed(2);
      return (
        `A página ${i + 1} tem texto demais para publicar (${mb} MB só de texto, sem contar os ` +
        `desenhos). Isso não costuma acontecer escrevendo à mão — se você colou texto de outro ` +
        `lugar, tente dividir em duas páginas.`
      );
    }

    for (const img of imagens) {
      if (pesoDoDocumento(img) > teto) {
        const mb = (pesoDoDocumento(img) / 1024 / 1024).toFixed(2);
        return (
          `Um desenho da página ${i + 1} ficou grande demais para guardar (${mb} MB). ` +
          `Gere esse desenho de novo — o produto encolhe as imagens ao gerar, e esta passou ` +
          `sem encolher.`
        );
      }
    }
  }
  return null;
}

/**
 * O plano de escritas de uma publicação, agora com as imagens contadas.
 *
 * Devolve as páginas leves e os documentos de imagem, na ordem de gravação: **imagem antes da
 * página que a referencia.** Se a gravação falhar no meio, sobra imagem sem página — que é
 * invisível e inofensiva. Na ordem inversa sobraria página apontando para imagem que não subiu, e
 * a criança abriria o quadrinho com quadros vazios.
 *
 * É a mesma lógica do `anuncioEhOUltimoPasso`: quando não dá para ser atômico, escolhe-se qual
 * lixo a falha deixa.
 */
export function separarTudo(paginas: Pagina[]): PaginaSeparada[] {
  return paginas.map((p, i) => separarImagens(p, i + 1));
}



